import { useEffect, useState } from 'react';
import { hot } from 'react-hot-loader/root';
import { datadogRum } from '@datadog/browser-rum';
import { useAuthAndLogin } from '@guildeducationinc/guild-auth';
import { DocumentTitleAnnouncer, Loading } from '@guildeducationinc/recess';
import ErrorPage from '@shared/ErrorPage/ErrorPage';
import { useMemberPlanContext } from '@src/context/MemberPlanContext';
import Routes from '@src/components/Routes/Routes';
import BaseModal from '@modals/BaseModal/BaseModal';
import GuildOptimizelyProvider from '@src/components/GuildOptimizelyProvider/GuildOptimizelyProvider';
import { PRODUCT, VERIFICATION_URL } from '@src/constants';
import GuildChat from '@src/remotes/GuildChat/GuildChat';
import MemberNav from '@src/remotes/MemberNav/MemberNav';
import HomeFooter from '@src/components/HomeFooter/HomeFooter';
import './app.scss';

const App: React.FC = () => {
  const {
    user,
    isAuthenticated,
    hasElevatedRoles,
    loading,
    hasAuthError,
    authError,
  } = useAuthAndLogin();
  const { planData } = useMemberPlanContext();
  const [isDataDogRumEnabled, setIsDataDogRumEnabled] = useState(false);

  useEffect(() => {
    if (
      !isDataDogRumEnabled &&
      process.env.REACT_APP_DATADOG_RUM_APP_ID &&
      process.env.REACT_APP_DATADOG_RUM_CLIENT_TOKEN
    ) {
      datadogRum.init({
        applicationId: process.env.REACT_APP_DATADOG_RUM_APP_ID,
        clientToken: process.env.REACT_APP_DATADOG_RUM_CLIENT_TOKEN,
        site: 'datadoghq.com',
        service: 'Student-Home-RUM',
        env: process.env.REACT_APP_DATADOG_RUM_ENV,
        allowedTracingUrls: [
          /https:\/\/home.guildacceptance\..*/,
          /https:\/\/home.guildeducation\..*/,
          /https:\/\/global-gql.*\.com\/graphql/,
          /https:\/\/graphql-gateway.*\.com\/graphql/,
          /http:localhost:8080*/,
        ],
        version: process.env.GITHUB_SHA,
        sessionSampleRate: 100,
        trackUserInteractions: true,
        trackFrustrations: true,
        trackResources: true,
        trackLongTasks: true,
        silentMultipleInit: true,
      });
      datadogRum.startSessionReplayRecording();

      setIsDataDogRumEnabled(true);
    }

    if (isDataDogRumEnabled) {
      datadogRum.setGlobalContext({ env: process.env.REACT_APP_DATADOG_RUM_ENV });
    }
  }, [isDataDogRumEnabled]);

  // custom gating function for EVS. If a user does not have an employeeId,
  // they are not verified and the user will always be redirected to EVS
  useEffect(() => {
    const redirectToEVS = isAuthenticated && !user?.employeeId && !hasElevatedRoles;
    if (redirectToEVS) {
      window.location.assign(VERIFICATION_URL!);
    }
  }, [isAuthenticated, user?.employeeId, hasElevatedRoles]);

  return (
    <GuildOptimizelyProvider user={user}>
      <DocumentTitleAnnouncer />
      {!loading && hasAuthError ? (
        <ErrorPage support={true} error={hasAuthError} authError={true} errorMessage={authError} />
      ) : (
        <Loading label='guild home' fullPageHeight={true} loaded={isAuthenticated}>
          <MemberNav
            analytics={window.analytics}
            productName={PRODUCT}
            planData={
              planData?.userPlan || {
                plan: {
                  products: {},
                },
              }
            }
          />

          <main id='main' tabIndex={-1}>
            <Routes />
            <GuildChat product={PRODUCT} />
          </main>

          <HomeFooter />

          <BaseModal />
        </Loading>
      )}
    </GuildOptimizelyProvider>
  );
};

export default hot(App);
